import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal, Typography } from 'antd';

import UserAvatar from './UserAvatar';
import { userLabel } from './UserChip';
import { normalizeId } from '../../utils/ids';

const { Text } = Typography;

/**
 * The "who is this?" card behind every interactive UserChip.
 *
 * Deliberately small: a modest 64px thumbnail beside the name, the email when
 * the projection carries one, and the raw id for the admin surfaces that need
 * to correlate a person with a log line. The avatar is a thumbnail, not a hero
 * image — this modal identifies a person, it is not a profile page.
 *
 * `action` is the calling surface's one piece of context — `{ label, to }`,
 * e.g. "Open item" from an attachment row. Following it closes the modal.
 *
 * Controlled by UserChip (`open` / `onClose`); it is never mounted until the
 * chip's avatar button is pressed, so no surface pays for a modal nobody opens.
 */
export default function UserInfoModal({ open, onClose, user, fallbackId, action }) {
	const name = userLabel(user);
	// Same substitution the chip makes: an unresolvable user is better named by
	// its id than by the 'Unknown user' sentinel.
	const displayName = name === 'Unknown user' && fallbackId ? fallbackId : name;
	const id = normalizeId(user?._id) || fallbackId || null;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={480}
			centered
			destroyOnClose
			className="user-info-modal"
		>
			<div className="user-info-modal__identity">
				<UserAvatar user={user} size={64} />
				<div className="user-info-modal__text">
					<div className="user-info-modal__name">{displayName}</div>
					{user?.email ? (
						<Text type="secondary" className="user-info-modal__email">{user.email}</Text>
					) : null}
					{id ? (
						<Text type="secondary" className="user-info-modal__id">{id}</Text>
					) : null}
				</div>
			</div>

			{action?.to ? (
				<div className="user-info-modal__actions">
					<Link to={action.to}>
						<Button type="primary" onClick={onClose}>{action.label || 'Open'}</Button>
					</Link>
				</div>
			) : null}
		</Modal>
	);
}
